import { CdkTrapFocus } from '@angular/cdk/a11y';
import { NgComponentOutlet } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  Injector,
  signal,
  type Type,
  untracked,
} from '@angular/core';

import { TranslocoPipe } from '@jsverse/transloco';
import { SkButtonComponent, SkIconComponent, SkWhatsNewService } from '@mercadoni/stark-ui';

import type { FuryRelease } from '../../core/interfaces';
import { provideFeatureScope } from '../../core/services/i18n/i18n-scope';
import { WHATS_NEW_STRINGS } from '../whats-new.strings';
import { resolveWhatsNewEvent, type WhatsNewEventDef } from '../whats-new-events';
import { WhatsNewVisibilityService } from '../whats-new-visibility.service';

/** Total intro length; the last keyframe (exit) ends here. Matches Nexus. */
export const WHATS_NEW_INTRO_MS = 2400;

const TABBABLE = 'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

let nextId = 0;

/**
 * Fury's "What's new" overlay: the Nexus layout and animation as a stateless view over
 * `SkWhatsNewService` (catalog, seen state, auto-open and tour hand-off stay in the engine).
 * Each release can pick an event look (see `whats-new-events.ts`); mounted once in the shell.
 */
@Component({
  selector: 'nf-whats-new-modal',
  imports: [NgComponentOutlet, CdkTrapFocus, SkIconComponent, SkButtonComponent, TranslocoPipe],
  providers: [provideFeatureScope('onboarding/whats-new')],
  templateUrl: './whats-new-modal.component.html',
  styleUrl: './whats-new-modal.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:keydown)': 'onKeydown($event)',
  },
})
export class NfWhatsNewModalComponent {
  protected readonly whatsNew = inject(SkWhatsNewService);
  private readonly visibility = inject(WhatsNewVisibilityService);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly injector = inject(Injector);

  protected readonly text = WHATS_NEW_STRINGS;
  protected readonly titleId = `nf-whats-new-title-${nextId++}`;

  protected readonly session = this.whatsNew.session;
  protected readonly activeItem = this.whatsNew.activeItem;
  protected readonly activeRelease = computed(
    () => this.whatsNew.activeRelease() as FuryRelease | null,
  );

  /** The engine's intro (first auto-open of the session) or one forced by the dev preview. */
  protected readonly showIntro = computed(
    () => (this.session()?.intro ?? false) || this.visibility.forcedIntro(),
  );

  protected readonly eventDef = computed<WhatsNewEventDef>(() =>
    resolveWhatsNewEvent(this.activeRelease()?.event),
  );
  protected readonly eventVars = computed(() => this.eventDef().cssVars);

  protected readonly introComponent = signal<Type<unknown> | null>(null);
  protected readonly decorationComponent = signal<Type<unknown> | null>(null);

  /** Key of the primary button's label, or `null` while no session is open. */
  protected readonly primaryLabelKey = computed<string | null>(() =>
    this.whatsNew.session() ? WHATS_NEW_STRINGS[this.whatsNew.primaryAction()] : null,
  );

  /** Element focused before the overlay opened; focus returns there on close. */
  private returnFocusTo: Element | null = null;
  private eventLoadToken = 0;

  constructor() {
    // Resolve the event's lazy intro/decoration components whenever the active event changes.
    effect(() => {
      const def = this.eventDef();
      untracked(() => {
        const token = ++this.eventLoadToken;
        this.introComponent.set(null);
        this.decorationComponent.set(null);
        void def.introComponent?.().then((c) => token === this.eventLoadToken && this.introComponent.set(c));
        void def.decorationComponent?.().then((c) => token === this.eventLoadToken && this.decorationComponent.set(c));
      });
    });

    // Focus stays inside the overlay for the whole session: on the overlay during the intro,
    // then on the card's first control once it is shown. Closing returns focus to the opener.
    effect(() => {
      const open = this.session() !== null;
      const intro = this.showIntro();
      untracked(() => {
        if (!open) {
          this.restoreFocus();
          return;
        }
        if (!this.returnFocusTo && typeof document !== 'undefined') {
          this.returnFocusTo = document.activeElement;
        }
        afterNextRender(() => this.focusInside(intro), { injector: this.injector });
      });
    });

    // The intro dismisses itself after its full length (immediately with reduced motion).
    effect((onCleanup) => {
      if (!this.showIntro()) return;
      const timer = setTimeout(
        () => this.dismissIntro(),
        prefersReducedMotion() ? 0 : WHATS_NEW_INTRO_MS,
      );
      onCleanup(() => clearTimeout(timer));
    });
  }

  protected dismissIntro(): void {
    if (this.visibility.forcedIntro()) {
      this.visibility.forcedIntro.set(false);
    } else {
      this.whatsNew.dismissIntro();
    }
  }

  protected close(): void {
    this.visibility.forcedIntro.set(false);
    this.whatsNew.close();
  }

  protected onBackdrop(): void {
    if (this.showIntro()) {
      this.dismissIntro();
    } else {
      this.close();
    }
  }

  protected onKeydown(event: KeyboardEvent): void {
    if (!this.session()) return;
    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        this.onBackdrop();
        break;
      case 'ArrowRight':
        if (this.showIntro()) return;
        event.preventDefault();
        this.whatsNew.next();
        break;
      case 'ArrowLeft':
        if (this.showIntro()) return;
        event.preventDefault();
        this.whatsNew.prev();
        break;
    }
  }

  protected openCta(url: string): void {
    try {
      const parsed = new URL(url);
      if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return;
      window.open(parsed.toString(), '_blank', 'noopener');
    } catch {
      // Not a valid absolute URL: ignore rather than navigate somewhere unintended.
    }
  }

  private focusInside(intro: boolean): void {
    const root = this.host.nativeElement;
    const overlay = root.querySelector<HTMLElement>('.nf-wn');
    if (!overlay) return;
    const card = root.querySelector<HTMLElement>('.nf-wn-card');
    const target = intro || !card ? overlay : (card.querySelector<HTMLElement>(TABBABLE) ?? card);
    if (!overlay.contains(document.activeElement) || document.activeElement === overlay) {
      target.focus();
    }
  }

  private restoreFocus(): void {
    const target = this.returnFocusTo;
    this.returnFocusTo = null;
    if (target instanceof HTMLElement && target.isConnected) target.focus();
  }
}

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}
